import React, { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

/**
 * Unified progress panel for optimization — used in both Workspace and Cases.
 *
 * Displays real-time progress by tracking the *actual* stage/title data
 * from backend polling (not hardcoded stage lists that drift out of sync).
 *
 * Props:
 *   progress: { stage, percent, title, desc } — latest backend poll data
 *   error: string | null — error message if pipeline failed
 *   compact: boolean — if true, renders smaller for case cards
 */

export function OptimizeProgressPanel({ progress, error, compact = false }) {
  const [displayPercent, setDisplayPercent] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  const [stageHistory, setStageHistory] = useState([]);
  const targetPercent = progress?.percent ?? 0;

  // Track stage history as new stages arrive from backend
  useEffect(() => {
    if (!progress?.title || !progress?.stage) return;
    setStageHistory(prev => {
      const lastEntry = prev[prev.length - 1];
      // Skip duplicate stages
      if (lastEntry && lastEntry.stage === progress.stage && lastEntry.title === progress.title) {
        return prev;
      }
      return [...prev, { stage: progress.stage, title: progress.title, desc: progress.desc }];
    });
  }, [progress?.stage, progress?.title, progress?.desc]);

  // Animate progress bar smoothly
  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayPercent(prev => {
        if (prev >= targetPercent) return targetPercent;
        const step = Math.max(0.3, (targetPercent - prev) * 0.12);
        return Math.min(targetPercent, prev + step);
      });
    }, 40);
    return () => clearInterval(interval);
  }, [targetPercent]);

  // Elapsed timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(((Date.now() - startRef.current) / 1000).toFixed(1));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const currentStage = progress?.stage ?? 0;
  const isComplete = currentStage >= 7;

  // Build the display stages from history + known final stages
  // The backend emits stages 1-7, each with a real title from the pipeline
  const KNOWN_STAGES = [
    { stage: 1, title: 'Preparing input' },
    { stage: 2, title: 'Extracting intent' },
    { stage: 3, title: 'Accessing cDM' },
    { stage: 4, title: 'Computing MSML Score' },
    { stage: 5, title: 'Applying safety guards' },
    { stage: 6, title: 'Finalizing results' },
    { stage: 7, title: 'Complete' },
  ];

  // Merge known stages with actual history (prefer history titles when available)
  const displayStages = KNOWN_STAGES.map(ks => {
    const fromHistory = stageHistory.find(h => h.stage === ks.stage);
    return {
      stage: ks.stage,
      title: fromHistory?.title || ks.title,
      desc: fromHistory?.desc || '',
    };
  });

  const barHeight = compact ? 'h-1.5' : 'h-2.5';
  const titleSize = compact ? 'text-[10px]' : 'text-sm';
  const stageSize = compact ? 'text-[9px]' : 'text-[11px]';
  const descSize = compact ? 'text-[8px]' : 'text-[9px]';
  const iconSize = compact ? 'w-3 h-3' : 'w-4 h-4';
  const gapSize = compact ? 'space-y-0.5' : 'space-y-1.5';
  const padding = compact ? '!p-3 space-y-2' : '!p-4 space-y-4';

  return (
    <div className={`msml-card ${padding} msml-slide-up`} data-testid="optimize-progress-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className={`${titleSize} font-semibold text-[var(--msml-text-primary)] tracking-tight`}>
          {isComplete ? 'Optimization complete' : 'Optimizing for human impact'}
        </span>
        <span className="flex items-center gap-1.5 text-[11px] tabular-nums text-[var(--msml-text-muted)] font-mono" data-testid="elapsed-timer">
          <Clock className="w-3 h-3" />
          {elapsed}s
        </span>
      </div>

      {/* Progress bar */}
      <div className={`relative ${barHeight} rounded-full bg-[var(--msml-surface-2)] overflow-hidden shadow-inner`} data-testid="progress-bar">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${displayPercent + 2}%`,
            opacity: 0.2,
            background: error ? 'var(--msml-danger)' : isComplete ? 'var(--msml-success)' : 'var(--msml-primary)',
            filter: 'blur(6px)',
          }}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${displayPercent}%`,
            background: error
              ? 'var(--msml-danger)'
              : isComplete
              ? 'linear-gradient(90deg, #10b981, #34d399)'
              : 'linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa)',
            boxShadow: error
              ? 'none'
              : isComplete
              ? '0 0 12px rgba(16, 185, 129, 0.5)'
              : '0 0 12px rgba(37, 99, 235, 0.5), 0 0 24px rgba(37, 99, 235, 0.2)',
          }}
        />
        {!isComplete && !error && (
          <div
            className="absolute inset-y-0 w-24 rounded-full animate-[optShimmer_1.8s_ease-in-out_infinite]"
            style={{
              left: `${Math.max(0, displayPercent - 10)}%`,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
            }}
          />
        )}
      </div>

      {/* Status + percent */}
      <div className="flex justify-between items-center -mt-2">
        <span className="text-[10px] text-[var(--msml-text-muted)]">
          {progress?.title || 'Initializing...'}
        </span>
        <span className="text-[10px] font-medium text-[var(--msml-text-secondary)] tabular-nums">
          {Math.round(displayPercent)}%
        </span>
      </div>

      {/* Stage list */}
      <div className={`${gapSize} pt-1`}>
        {displayStages.map((stage) => {
          const done = currentStage > stage.stage;
          const active = currentStage === stage.stage;

          return (
            <div
              key={stage.stage}
              className={`flex items-start gap-2 ${compact ? 'py-0.5 px-1.5' : 'py-1 px-2'} rounded-lg transition-all duration-400 ${
                active ? 'bg-[var(--msml-primary)]/8 border border-[var(--msml-primary)]/15' :
                done ? 'opacity-70' : 'opacity-25'
              }`}
              data-testid={`stage-${stage.stage}`}
            >
              {done ? (
                <CheckCircle2 className={`${iconSize} text-[var(--msml-success)] flex-shrink-0 mt-0.5`} />
              ) : active ? (
                <Loader2 className={`${iconSize} text-[var(--msml-primary)] flex-shrink-0 mt-0.5 animate-spin`} />
              ) : (
                <div className={`${iconSize} rounded-full border border-[var(--msml-border)] flex-shrink-0 mt-0.5`} />
              )}
              <div className="min-w-0">
                <p className={`${stageSize} leading-tight ${
                  active ? 'text-[var(--msml-text-primary)] font-semibold' :
                  done ? 'text-[var(--msml-text-secondary)]' : 'text-[var(--msml-text-muted)]'
                }`}>
                  {stage.title}
                </p>
                {active && stage.desc && !compact && (
                  <p className={`${descSize} text-[var(--msml-text-muted)] mt-0.5 leading-snug`}>
                    {stage.desc}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-[var(--msml-danger)]/10 border border-[var(--msml-danger)]/20" data-testid="progress-error">
          <AlertCircle className="w-4 h-4 text-[var(--msml-danger)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[11px] text-[var(--msml-danger)] font-medium">Optimization failed</p>
            <p className="text-[10px] text-[var(--msml-text-muted)] mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Complete */}
      {isComplete && !error && (
        <div className="flex items-center gap-2 pt-1" data-testid="progress-complete">
          <CheckCircle2 className="w-4 h-4 text-[var(--msml-success)]" />
          <span className="text-[11px] text-[var(--msml-success)] font-medium">
            Completed in {elapsed}s
          </span>
        </div>
      )}

      <style>{`
        @keyframes optShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}
